import {
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { format, parseISO, startOfMonth } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api';
import { clampJobsDateRange, normalizeJobsDateRange } from '../../helpers/jobs';
import { patchOverviewCardsForNotification } from '../../helpers/jobLivePatch';
import { seedJobLiveInProgress } from '../../helpers/jobLiveInProgress';
import {
    buildPrinterOverviewCards,
    buildPrinterStatsQuery,
    defaultPrintersOverviewPeriod,
    PRINTERS_OVERVIEW_PERIOD_MODES,
    printersInProgressSet,
    resolvePrintersOverviewPeriod,
    summarizeMaintenancesByPrinter,
} from '../../helpers/printersOverview';
import { isJobIntegrationLiveNotification, subscribeLiveNotifications } from '../../helpers/liveNotifications';
import { useLiveUpdateHighlights } from '../../helpers/liveUpdateHighlight';
import { filterActivePrinters, sortPrintersByName } from '../../helpers/printers';
import Loading from '../Loading';
import PrinterOverviewCard from './PrinterOverviewCard';

export default function PrintersOverviewPanel() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const defaultPeriod = useMemo(() => defaultPrintersOverviewPeriod(), []);
    const defaultCustomPeriod = useMemo(() => {
        const today = new Date();
        return {
            from: format(startOfMonth(today), 'yyyy-MM-dd'),
            to: format(today, 'yyyy-MM-dd'),
        };
    }, []);

    const [periodFrom, setPeriodFrom] = useState(defaultPeriod.from);
    const [periodTo, setPeriodTo] = useState(defaultPeriod.to);
    const [appliedPeriod, setAppliedPeriod] = useState(defaultPeriod);
    const [cardsPeriod, setCardsPeriod] = useState(defaultPeriod);
    const [periodMode, setPeriodMode] = useState('panel_period');
    const [customFrom, setCustomFrom] = useState(defaultCustomPeriod.from);
    const [customTo, setCustomTo] = useState(defaultCustomPeriod.to);
    const [loading, setLoading] = useState(true);
    const [cardsLoading, setCardsLoading] = useState(false);
    const [cards, setCards] = useState([]);
    const isInitialLoad = useRef(true);
    const { isPrinterHighlighted } = useLiveUpdateHighlights(isJobIntegrationLiveNotification);

    const loadOverview = useCallback((mode, panelPeriod, customPeriodFrom, customPeriodTo) => {
        const resolved = resolvePrintersOverviewPeriod({
            mode,
            appliedPeriod: panelPeriod,
            customFrom: customPeriodFrom,
            customTo: customPeriodTo,
        });
        if (resolved.error) {
            Swal.fire('Período inválido', resolved.error, 'warning');
            return Promise.resolve();
        }

        const statsQuery = buildPrinterStatsQuery({
            finishedFrom: resolved.finishedFrom,
            finishedTo: resolved.finishedTo,
        });

        if (isInitialLoad.current) {
            setLoading(true);
        } else {
            setCardsLoading(true);
        }

        return Promise.all([
            api.get('printers?page=1&limit=200'),
            api.get(`jobs/printer-stats?${statsQuery}`),
            api.get('jobs?status=em_andamento&limit=200'),
            api.get('printer-maintenances?page=1&limit=500'),
        ])
            .then(([printersRes, statsRes, inProgressRes, maintenancesRes]) => {
                const printers = sortPrintersByName(filterActivePrinters(printersRes.data.printers || []));
                const stats = statsRes.data.data?.printers || [];
                const inProgressJobs = inProgressRes.data.data?.jobs || [];
                const maintenances = maintenancesRes.data.data?.maintenances || [];
                const maintenanceSummary = summarizeMaintenancesByPrinter(
                    maintenances,
                    resolved.finishedFrom,
                    resolved.finishedTo,
                );
                const inProgressSet = printersInProgressSet(inProgressJobs);
                seedJobLiveInProgress(inProgressJobs);
                setCards(
                    buildPrinterOverviewCards(printers, stats, maintenanceSummary, inProgressSet),
                );
                setCardsPeriod(resolved.period);
            })
            .catch(() => {
                Swal.fire('Erro', 'Não foi possível carregar o painel de impressoras.', 'error');
            })
            .finally(() => {
                setCardsLoading(false);
                if (isInitialLoad.current) {
                    isInitialLoad.current = false;
                    setLoading(false);
                }
            });
    }, []);

    useEffect(() => {
        if (periodMode !== 'custom') {
            loadOverview(periodMode, appliedPeriod, customFrom, customTo);
        }
    }, [periodMode, appliedPeriod, loadOverview]);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }
            setCards((prev) => patchOverviewCardsForNotification(prev, detail));
        });
    }, []);

    const handleApplyPanelPeriod = () => {
        const clamped = clampJobsDateRange(periodFrom, periodTo);
        setPeriodFrom(clamped.start);
        setPeriodTo(clamped.end);
        setAppliedPeriod({ from: clamped.start, to: clamped.end });
    };

    const handlePeriodModeChange = (_event, value) => {
        if (value) {
            setPeriodMode(value);
        }
    };

    const handleCustomDateFromChange = (value) => {
        setCustomFrom(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(value, customTo || value);
        setCustomFrom(normalized.start);
        setCustomTo(normalized.end);
    };

    const handleCustomDateToChange = (value) => {
        setCustomTo(value);
        if (!value) {
            return;
        }
        const normalized = normalizeJobsDateRange(customFrom || value, value);
        setCustomFrom(normalized.start);
        setCustomTo(normalized.end);
    };

    const handleApplyCustomPeriod = () => {
        loadOverview('custom', appliedPeriod, customFrom, customTo);
    };

    const panelPeriodLabel = useMemo(() => {
        try {
            const from = format(parseISO(appliedPeriod.from), 'dd/MM/yyyy');
            const to = format(parseISO(appliedPeriod.to), 'dd/MM/yyyy');
            return `${from} — ${to}`;
        } catch {
            return '';
        }
    }, [appliedPeriod]);

    const cardsPeriodLabel = useMemo(() => {
        if (periodMode === 'all_time') {
            return 'Todo o tempo';
        }
        if (!cardsPeriod.from || !cardsPeriod.to) {
            return '';
        }
        try {
            const from = format(parseISO(cardsPeriod.from), 'dd/MM/yyyy');
            const to = format(parseISO(cardsPeriod.to), 'dd/MM/yyyy');
            return `${from} — ${to}`;
        } catch {
            return '';
        }
    }, [periodMode, cardsPeriod]);

    if (loading) {
        return <Loading />;
    }

    return (
        <Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'stretch' : 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 1.5,
                }}
            >
                <Typography variant="subtitle1" fontWeight={600}>
                    Visão do período
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    color="primary"
                    exclusive
                    value={periodMode}
                    onChange={handlePeriodModeChange}
                    sx={{ flexWrap: 'wrap' }}
                >
                    {PRINTERS_OVERVIEW_PERIOD_MODES.map((option) => (
                        <ToggleButton key={option.value} value={option.value}>
                            {option.label}
                        </ToggleButton>
                    ))}
                </ToggleButtonGroup>
            </Box>

            {periodMode === 'panel_period' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Usa o período do filtro da lista
                    {panelPeriodLabel ? ` (${panelPeriodLabel})` : ''}
                    . Clique em Atualizar no filtro para aplicar novas datas.
                </Typography>
            )}

            {periodMode === 'custom' && (
                <Grid container spacing={2} sx={{ mb: 1.5 }}>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data inicial"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={customFrom}
                            onChange={(e) => handleCustomDateFromChange(e.target.value)}
                            inputProps={{ max: customTo || undefined }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            label="Data final"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={customTo}
                            onChange={(e) => handleCustomDateToChange(e.target.value)}
                            inputProps={{ min: customFrom || undefined }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Button
                            variant="contained"
                            onClick={handleApplyCustomPeriod}
                            disabled={cardsLoading}
                        >
                            Atualizar período
                        </Button>
                    </Grid>
                </Grid>
            )}

            {cardsPeriodLabel && periodMode !== 'panel_period' && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                    Exibindo: {cardsPeriodLabel}
                </Typography>
            )}

            {cardsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={28} />
                </Box>
            )}

            {cards.length === 0 ? (
                <Typography color="text.secondary" sx={{ mb: 2, opacity: cardsLoading ? 0.5 : 1 }}>
                    Nenhuma impressora cadastrada. Adicione uma impressora na lista abaixo.
                </Typography>
            ) : (
                <Grid container spacing={2} sx={{ opacity: cardsLoading ? 0.5 : 1, mb: 2 }}>
                    {cards.map((printer) => (
                        <Grid item xs={12} sm={6} md={4} key={printer.printer_uuid}>
                            <PrinterOverviewCard
                                printer={printer}
                                period={cardsPeriod}
                                highlighted={isPrinterHighlighted(printer.printer_uuid)}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    mt: 3,
                    pt: 3,
                    borderTop: 1,
                    borderColor: 'divider',
                }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Painel de impressoras
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Período do filtro: {panelPeriodLabel}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <TextField
                        label="De"
                        type="date"
                        size="small"
                        value={periodFrom}
                        onChange={(e) => setPeriodFrom(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Até"
                        type="date"
                        size="small"
                        value={periodTo}
                        onChange={(e) => setPeriodTo(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button variant="contained" onClick={handleApplyPanelPeriod}>
                        Atualizar
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
